import React, { Fragment } from 'react';
import { Breakpoint } from 'react-socks';
import { common, query } from '@kurtosys/ksys-app-template';
import { IFormatter } from '@kurtosys/types/data/IFormatter.js';
import { GlobalDisclaimerLevel } from '@kurtosys/types/disclaimers/GlobalDisclaimerLevel.js';
import { observer } from 'mobx-react';
import * as Recharts from 'recharts';

import { TLabelFormatter } from '../../../models/charting/TLabelFormatter.js';
import { ITextProps, ITooltipProps, IXAxisProps, TChartElement } from '../../../models/index.js';
import { InjectedStyledComponent, KEYS, ResponsiveComponentBase } from '../../../shared/index.js';
import { AccessibilityContext } from '../../base/Accessibility/Accessibility.js';
import { IAccessibilityContextValue } from '../../base/Accessibility/models/index.js';
import { ITranslationContextValue } from '../../base/Translate/models/ITranslationContextValue.js';
import { TranslationContext } from '../../base/Translate/Translate.js';
import Disclaimers from '../../overview/Disclaimers/Disclaimers.js';

import DefaultLegendContent from './components/DefaultLegendContent/index.js';
import DefaultTooltipContent from './components/DefaultTooltipContent/DefaultTooltipContent.js';
import {
	IChartBaseConfiguration,
	IChartBaseProps,
	IChartBaseState,
	IMappedColor,
	ISingleChart,
} from './models/index.js';
import ChartSummary from './styledComponents/ChartSummary.js';
import DisclaimersWrapper from './styledComponents/DisclaimersWrapper.js';
import Wrapper from './styledComponents/Wrapper.js';
import { CHART_BASE_CATEGORY_KEY, CHART_BASE_COMPONENT_KEY } from './ChartBase.meta.js';
import {
	applyAnimationLogic,
	applyCustomTickLabel,
	applyCustomTickValues,
	applyDataLength,
	applyFormatters,
	applyGlobalDisclaimers,
	applyLabelQueries,
	applyRebaseToComposedChartProps,
	applySeriesLabels,
	applyTickPivotPoint,
	applyTranslations,
	applyTranslationsToComposedChartProps,
	calculateBarChartHeight,
	getChartSyncTicks,
	getDynamicReferenceLines,
	validateLegendElement,
} from './ChartBaseFunctions.js';

const ARROW_KEYS = [KEYS.UP_ARROW, KEYS.LEFT_ARROW, KEYS.RIGHT_ARROW, KEYS.DOWN_ARROW];

/**
 * A base component for creating charts using Recharts library.
 * @component
 * @param {Object} props - The component props.
 * @param {string} props.className - The class name for the component.
 * @param {Array} props.breakpoints - An array of breakpoints for the chart.
 * @returns {JSX.Element} A React component that renders a chart.
 */
export class ChartBase extends ResponsiveComponentBase<IChartBaseProps, IChartBaseState, IChartBaseConfiguration> {
	categoryKey = CHART_BASE_CATEGORY_KEY;
	componentKey = CHART_BASE_COMPONENT_KEY;

	refGroupKeys: string[] = [];
	focusRefs = new Map<string, React.RefObject<SVGElement>[]>();
	// ts was complaining on possible null values, there are too many instances to do undefined checks and
	// run the possibility of introducing bugs with the focus order
	focusedGroupIndex = 0;
	// Setting to negative to allow the right arrow to move to 0 index, the left will handle to place on last index
	focusedItemIndex = -1;
	hideDots = false;
	chartContainer: HTMLDivElement | null = null;

	valueFormatter: Partial<IFormatter> | undefined;

	constructor(props: IChartBaseProps) {
		super(props);
	}

	/**
	 * Returns a LabelList element for rendering a label list in a chart.
	 * @param {Object} props - The props object containing the label list configuration.
	 * @param {string} props.dataKey - The data key to use for the label list.
	 * @param {Object} props.labelList - The configuration object for the label list.
	 * @returns {JSX.Element|undefined} A JSX element for rendering the label list, or undefined if no label list is configured.
	 */
	getLabelList(props: any): JSX.Element | undefined {
		const labelListProps = props && props.labelList;
		if (labelListProps) {
			let labelFormatter: TLabelFormatter | undefined;
			if (common.commonUtils.isNullOrUndefined(labelListProps.dataKey)) {
				labelListProps.dataKey = props.dataKey;
			}
			if (!common.commonUtils.isNullOrUndefined(labelListProps.formatter)) {
				labelFormatter = (label: string | number) => {
					return common.commonUtils.formatter(labelListProps.formatter as IFormatter, label);
				};
			}
			// eslint-disable-next-line @typescript-eslint/naming-convention
			const LabelListElement = Recharts['LabelList'] as any;
			if (labelListProps.color) {
				return <LabelListElement {...labelListProps} formatter={labelFormatter} fill={labelListProps.color} />;
			}
			return <LabelListElement {...labelListProps} formatter={labelFormatter} />;
		}
		return undefined;
	}

	/**
	 * Returns a `Label` element for the chart.
	 * @param {Object} props - The props for the chart base component.
	 * @returns {JSX.Element} A `Label` element.
	 */
	getLabel(props: any) {
		const labelProps = props && props.label;
		if (labelProps) {
			const LabelElement = Recharts['Label'] as any;
			return <LabelElement {...labelProps} />;
		}
	}

	/**
	 * Returns an array of `Cell` elements for the chart.
	 * @param {Object} props - The props for the chart base component.
	 * @param {Array} [data=[]] - The data for the chart.
	 * @returns {Array} An array of `Cell` elements.
	 */
	getCells(props: any, data: any[] = []) {
		const colors = props && props.colors;
		if (!common.commonUtils.isNullOrEmpty(colors)) {
			// eslint-disable-next-line @typescript-eslint/naming-convention
			const CellElement = Recharts['Cell'] as any;
			const cells = [];

			if (typeof colors[0] === 'string') {
				// Color Strings
				const count = props && props.count;
				for (let idx = 0; idx < count; idx++) {
					cells.push(<CellElement key={`cell-${idx}`} fill={colors[idx % colors.length]} />);
				}
			} else {
				// Mapped Colors
				data.forEach((dataItem: any, idx: number) => {
					const color = colors.find((c: IMappedColor) => c.key === dataItem.name);
					cells.push(<CellElement key={`cell-${idx}`} fill={color && color.color} />);
				});
			}

			return cells;
		}
	}

	/**
	 * Returns the props for the responsive container element.
	 * @param {IChartBaseProps} [props] - The props for the chart base component.
	 * @returns {Object} The props for the responsive container element.
	 */
	getResponsiveContainerProps(props?: IChartBaseProps) {
		if (props) {
			const { responsiveContainerProps = {}, composedChartProps = {}, elements = [] } = props;
			if (!responsiveContainerProps.forceHeight) {
				const barChartHeight = calculateBarChartHeight(composedChartProps, elements);
				if (barChartHeight) {
					responsiveContainerProps.height = barChartHeight;
				}
			}
			return responsiveContainerProps;
		}
		return {};
	}

	/**
	 * Returns the accessibility attributes for the chart container element.
	 * @param {string} id - The ID of the chart container element.
	 * @param {string} [summary] - The summary text for the chart.
	 * @param {string} [chartAccessibilitySummary] - The accessibility summary text for the chart.
	 * @returns {Object} The props for the chart container element.
	 */
	getChartContainerProps(id: string, summary?: string, chartAccessibilitySummary?: string) {
		if (summary) {
			return {
				'aria-labelledby': id,
			};
		}

		if (chartAccessibilitySummary && !summary) {
			return {
				'aria-label': chartAccessibilitySummary,
			};
		}

		return {
			'aria-hidden': true,
		};
	}

	/**
	 * Returns the data keys that the series are based on
	 * @param {ISingleChart<any>} chartProps - The chart props
	 * @returns {Array} A string array that has the configured data keys for the series
	 */
	getSeriesDataKeys = (chartProps: ISingleChart<any>) => {
		return (chartProps?.seriesProps || []).map((seriesProp) => seriesProp.dataKey);
	};

	/**
	 * Adds the ref element based of the data key and index to the necessary variables that will control the focus order of the elements
	 * @param {string} dataKey - The data key of the series
	 * @param {number} index - The index of the elements
	 * @param {React.RefObject<SVGElement>} ref - The ref element that should receive focus
	 */
	addRefToFocusRefs(dataKey: string, index: number, ref: React.RefObject<SVGElement>) {
		if (!this.focusRefs.has(dataKey)) {
			this.refGroupKeys.push(dataKey);
			this.focusRefs.set(dataKey, []);
		}

		const focusRef = this.focusRefs.get(dataKey);
		if (focusRef) {
			if (focusRef.length >= index) {
				focusRef[index] = ref;
			} else {
				focusRef.push(ref);
			}
		}
	}

	/**
	 * Returns a callback function called `renderDot` that renders a circle SVG element with specific properties.
	 * The rendered element has a reference to manage dot focus and an accessibility label to announce the value of the dot.
	 *
	 * @param translationContext - An object containing a `translate` function and a `culture` string.
	 * @param xAxis - A chart element representing the X-axis.
	 * @param tooltip - A chart element representing the tooltip.
	 * @returns A callback function that renders a circle SVG element.
	 */
	renderDotWrapper = (
		translationContext: ITranslationContextValue,
		{
			xAxis = { type: 'XAxis' },
			tooltip = { type: 'Tooltip' },
		}: {
			xAxis?: TChartElement;
			tooltip?: TChartElement;
		},
	) => {
		/**
		 * Renders a circle SVG element with specific properties.
		 *
		 * @param props - The element props that recharts will pass in.
		 * @returns A circle SVG element.
		 */
		const renderDot = (props: any) => {
			if (!props) return null;

			const { key, cx, cy, stroke, strokeWidth, r, value, dataKey, index, fill, name, payload } = props;
			const dotRef = React.createRef<SVGCircleElement>();

			this.addRefToFocusRefs(dataKey, index, dotRef);
			const seriesName = name ?? dataKey;

			return (
				<circle
					key={key}
					ref={dotRef}
					cx={cx}
					cy={cy}
					r={r}
					stroke={this.hideDots ? 'rgba(0,0,0,0)' : stroke}
					strokeWidth={strokeWidth}
					fill={this.hideDots ? 'rgba(0,0,0,0)' : fill}
					aria-label={this.getElementAriaLabel(translationContext, {
						xAxis,
						payload,
						tooltip,
						value,
						seriesName,
					})}
					role="img"
					tabIndex={-1}
				/>
			);
		};

		return renderDot;
	};

	getPath = (x: number, y: number, width: number, height: number) => {
		// width will be negative if the bar is a negative value in the vertical orientation
		if (width < 0) {
			width = Math.abs(width);
			return `M ${x},${y} h -${width} v ${height} h ${width} Z`;
		}
		return `M ${x},${y} h ${width} v ${height} h -${width} Z`;
	};

	/**
	 * Renders rectangles in a chart.
	 * @param translationContext - An object containing translation-related properties.
	 * @param xAxis - The x-axis props of the chart.
	 * @param tooltip - The tooltip props of the chart.
	 * @returns A callback function used to render rectangles in a chart.
	 */
	renderRectangleWrapper = (
		translationContext: ITranslationContextValue,
		{
			xAxis = { type: 'XAxis' },
			tooltip = { type: 'Tooltip' },
		}: { xAxis?: TChartElement; tooltip?: TChartElement },
	) => {
		/**
		 * Renders a rectangle.
		 * @param props - The properties of the rectangle.
		 * @returns The rendered rectangle element.
		 */
		const renderRectangle = (props: any) => {
			if (!props) return;

			const { fill, x, y, width, height, tooltipPayload, index, value, name, payload } = props;
			if (tooltipPayload && tooltipPayload.length > 0) {
				// I could not find an example of multiple tooltipPayloads, thus I will use the first to get the dataKey
				const dataKey = tooltipPayload[0].dataKey;
				const seriesName = tooltipPayload[0].name;
				const shapeRef = React.createRef<SVGPathElement>();
				this.addRefToFocusRefs(dataKey, index, shapeRef);

				// width will be NaN if there is no data point. We should not be rendering an element
				if (!isNaN(width)) {
					return (
						<path
							ref={shapeRef}
							d={this.getPath(x, y, width, height)}
							cx={x}
							cy={y}
							stroke="none"
							fill={fill}
							tabIndex={-1}
							aria-label={this.getElementAriaLabel(translationContext, {
								xAxis,
								payload,
								tooltip,
								value,
								seriesName,
							})}
							role="img"
						/>
					);
				}
			} else {
				console.warn(
					`ChartBase.renderRectangle: Could not determine the dataKey due to no tooltip payload being received, keyboard functionality might not work`,
				);
			}
		};
		return renderRectangle;
	};

	getElementAriaLabel = (
		translationContext: ITranslationContextValue,
		{
			xAxis,
			payload,
			tooltip,
			value,
			seriesName,
		}: {
			xAxis: TChartElement;
			payload: any;
			tooltip: TChartElement;
			value: string | number;
			seriesName: string;
		},
	) => {
		const { props: xAxisProps = {} } = xAxis;
		const { dataKey: xAxisDataKey, tickFormatter: xAxisTickFormatter } = xAxisProps as IXAxisProps;

		let xAxisText = '';
		const { props: tooltipProps = {} } = tooltip;
		const { labelFormatter } = tooltipProps as ITooltipProps;
		if (typeof xAxisDataKey === 'string' && payload) {
			const tickValue = payload[xAxisDataKey] || 'No Value';
			// use the tooltip label formatter if it exists, otherwise use the xAxisTickFormatter as a fallback
			if (typeof labelFormatter === 'function') {
				xAxisText = labelFormatter(tickValue);
			} else {
				xAxisText = typeof xAxisTickFormatter === 'function' ? xAxisTickFormatter(tickValue) : tickValue;
			}
		}

		let formattedValue = value;
		if (this.valueFormatter) {
			formattedValue = common.commonUtils.formatter(this.valueFormatter, value);
		}

		const placeholders = {
			value: formattedValue,
			seriesName,
			xAxisText,
		};

		const defaultAriaLabel = `{xAxisText}, {value}. {seriesName}.`;
		const dotAriaLabel = 'charting.activeAriaLabel';
		let ariaLabel = translationContext.translate(dotAriaLabel, placeholders);
		if (ariaLabel === dotAriaLabel) {
			ariaLabel = translationContext.translate(defaultAriaLabel, placeholders);
		}

		return ariaLabel;
	};

	onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
		const { key } = event;
		if (ARROW_KEYS.indexOf(key as KEYS) !== -1 && this.focusRefs.size > 0) {
			switch (key) {
				case 'ArrowUp': {
					event.preventDefault();
					if (this.focusedGroupIndex === 0) {
						this.focusedGroupIndex = this.focusRefs.size - 1;
					} else {
						this.focusedGroupIndex -= 1;
						const curr = this.focusRefs.get(this.refGroupKeys[this.focusedGroupIndex]);
						if (curr) {
							this.focusedItemIndex =
								curr.length < this.focusedItemIndex ? curr.length : this.focusedItemIndex;
						} else {
							this.focusedItemIndex = 0;
						}
					}
					break;
				}
				case 'ArrowDown': {
					event.preventDefault();
					if (this.focusedGroupIndex >= this.refGroupKeys.length - 1) {
						this.focusedGroupIndex = 0;
					} else {
						this.focusedGroupIndex += 1;
						const curr = this.focusRefs.get(this.refGroupKeys[this.focusedGroupIndex]);
						if (curr) {
							this.focusedItemIndex =
								curr.length < this.focusedItemIndex ? curr.length : this.focusedItemIndex;
						}
					}
					break;
				}
				case 'ArrowLeft': {
					event.preventDefault();
					if (this.focusedItemIndex <= 0) {
						const curr = this.focusRefs.get(this.refGroupKeys[this.focusedGroupIndex]);
						if (curr) {
							this.focusedItemIndex = curr.length - 1;
						}
					} else {
						this.focusedItemIndex -= 1;
					}
					break;
				}
				case 'ArrowRight': {
					event.preventDefault();
					const curr = this.focusRefs.get(this.refGroupKeys[this.focusedGroupIndex]);
					if (curr) {
						if (this.focusedItemIndex >= curr.length - 1) {
							this.focusedItemIndex = 0;
						} else {
							this.focusedItemIndex += 1;
						}
					}
					break;
				}
				default:
					break;
			}

			// Set the focus on the next element, then simulate a mouse move to the coords of the element to trigger the tooltip to display
			const curr = this.focusRefs.get(this.refGroupKeys[this.focusedGroupIndex]);
			if (curr) {
				// Up and down arrows will not fix the index from -1
				if (this.focusedItemIndex === -1) {
					this.focusedItemIndex = 0;
				}
				const nextFocus = curr[this.focusedItemIndex].current;
				if (nextFocus) {
					nextFocus.focus();
					// Will need to get the chart wrapper to be able to provide an accurate clientX and clientY
					if (this.chartContainer) {
						const box = this.chartContainer.getBoundingClientRect();
						const html = this.chartContainer.ownerDocument.documentElement;

						// Basically reversing recharts offset calculation to get the tooltip render close to the active element
						const offsetY = html.clientTop + window.scrollY - box.top;
						const offsetX = html.clientLeft + window.scrollX - box.left;

						const cx = Number(nextFocus.getAttribute('cx')) - offsetX;
						const cy = Number(nextFocus.getAttribute('cy')) - offsetY;

						if (isNaN(cx) && isNaN(cy)) {
							console.warn(
								`ChartBase.onKeyDown: Could not determine the x: ${cx} and y: ${cy} position of element`,
							);
							return;
						}
						// Simulate the mouse hover on the element, to allow the chart to render the tooltip
						const mouseMoveEvent = new MouseEvent('mousemove', {
							view: window,
							bubbles: true,
							cancelable: true,
							clientX: cx,
							clientY: cy,
						});
						nextFocus.dispatchEvent(mouseMoveEvent);
					}
				}
			}
		}
	};

	/**
	 * A base component for rendering charts using Recharts library.
	 * @param {Object} props - The props object.
	 * @param {Object} props.composedChartProps - The props for the composed chart.
	 * @param {Array} props.elements - The array of chart elements.
	 * @param {Object} props.rebaseOptions - The options for rebasing the chart props.
	 * @param {string} props.chartBaseComponent - The base component for the chart.
	 * @param {string} props.appKey - The key for the app.
	 * @param {Object} props.queryStore - The query store object.
	 * @param {Object} props.disclaimersProps - The props for the disclaimers.
	 * @param {Array} props.cleanConfigurations - The array of clean configurations.
	 * @param {Object} translationContext - The translation context object.
	 * @param {Object} accessibilityContext - The accessibility context object.
	 * @returns {JSX.Element} The chart base component.
	 */
	renderChart = (
		props: IChartBaseProps | undefined,
		translationContext: ITranslationContextValue,
		accessibilityContext: IAccessibilityContextValue,
	) => {
		if (!props) {
			return null;
		}

		// Fix build issue. bad typings
		const responsiveContainerProps: any = this.getResponsiveContainerProps(props);
		const {
			composedChartProps = {},
			elements = [],
			rebaseOptions,
			chartBaseComponent = 'ComposedChart',
			appKey,
			queryStore,
		} = props;
		let { disclaimersProps } = props;

		applyTranslationsToComposedChartProps(composedChartProps, translationContext);
		applyRebaseToComposedChartProps(composedChartProps, rebaseOptions);

		const { cleanConfigurations } = props;
		let dataLength: number | undefined;
		let data = composedChartProps && composedChartProps.data;

		elements && elements.push(...getDynamicReferenceLines(elements, composedChartProps, cleanConfigurations));
		let chartProps: ISingleChart<any> = {};
		let combinedTicks: number[] = [];
		let combinedTickCount = 0;
		let syncTicks: { ticks: any[]; tickCount: number } | undefined;
		let chartSummary = undefined;
		if (cleanConfigurations) {
			if (cleanConfigurations.length > 0) {
				chartProps = cleanConfigurations[cleanConfigurations.length - 1];
				// Set up the data with the rebased composed chart props data
				data = data || chartProps.data;
				dataLength = data && data.length;
				chartSummary = cleanConfigurations[1] && cleanConfigurations[1].summary;
			}

			syncTicks = getChartSyncTicks(chartProps, elements, composedChartProps);
			// Used to allow the x and y axis to have the same ticks
			if (syncTicks) {
				combinedTicks = syncTicks.ticks;
				combinedTickCount = syncTicks.tickCount;
			}
			// Save the value formatter for the aria label, it can be configured in the bar label list or in the tooltip
			this.valueFormatter =
				chartProps.commonSeriesProps?.labelList?.formatter || chartProps.elementOverrides?.tooltip?.formatter;
		}

		let hasSuperscriptDisclaimer = false;
		// appKey is required so that we don't get all disclaimers returning when they are not setup
		if (appKey) {
			hasSuperscriptDisclaimer = applyGlobalDisclaimers(elements, data, queryStore, appKey);
			query.queryUtils.globalDisclaimers.initializeGlobalDisclaimers();
			const globalDisclaimerStore = query.queryUtils.globalDisclaimers.getGlobalDisclaimerStore();
			if (globalDisclaimerStore) {
				const disclaimers = [];
				// gets all disclaimers to show at the bottom of the chart
				disclaimers.push(...(globalDisclaimerStore.get(GlobalDisclaimerLevel.app, appKey) as any[]));
				if (disclaimers && disclaimers.length > 0) {
					disclaimersProps = disclaimersProps || {};
					disclaimersProps.disclaimers = disclaimersProps.disclaimers || [];
					disclaimersProps.disclaimers.push(...disclaimers);
				}
			}
		}

		const ChartBaseComponent = Recharts[chartBaseComponent] as any;
		const chartAccessibilitySummary = accessibilityContext.accessibilityText('charting.summary', '');
		const translatedNavigationSummary = translationContext.translate('charting.navigationSummary');
		const defaultNavigationSummary = accessibilityContext.accessibilityText(
			'charting.navigationSummary',
			`
			Instructions for navigating the chart:
			To move between series, use the up and down arrow keys.
			To move between items within a series, use the left and right arrow keys.
			When you press the up arrow key, the focus moves to the previous series. If you are already on the first series, the focus moves to the last series.
			When you press the down arrow key, the focus moves to the next series. If you are already on the last series, the focus moves to the first series.
			When you press the left arrow key, the focus moves to the previous item within the current series. If you are already on the first item, the focus moves to the last item in the series.
			When you press the right arrow key, the focus moves to the next item within the current series. If you are already on the last item, the focus moves to the first item in the series.
			`,
		);

		const navigationSummary =
			translatedNavigationSummary && translatedNavigationSummary !== 'charting.navigationSummary'
				? translatedNavigationSummary
				: defaultNavigationSummary;

		const summaryId = chartSummary ? `chart-summary-${common.commonUtils.generateGuid()}` : '';
		/** Used for keyboard and a11y support **/
		const xAxis = elements.find((value) => value.type === 'XAxis');
		// Tooltip is used here to get the formatter used for the tooltip
		const tooltip = elements.find((value) => value.type === 'Tooltip');
		/** ** **/
		return (
			<Fragment>
				<Fragment>
					{chartSummary && <ChartSummary id={summaryId} title={chartSummary} level={2} />}
					<div
						tabIndex={0}
						onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>) => this.onKeyDown(event)}
						{...this.getChartContainerProps(
							summaryId,
							chartSummary,
							`${chartAccessibilitySummary} ${navigationSummary}`,
						)}
					>
						<Recharts.ResponsiveContainer {...responsiveContainerProps} minHeight="600px">
							<ChartBaseComponent
								{...composedChartProps}
								ref={(node: any) => (this.chartContainer = node?.container ?? null)}
							>
								{elements.map((element, elementIndex) => {
									// reset element.props.length to data length
									applyDataLength(element, dataLength);
									applyTranslations(element, translationContext);
									// if not using equal axis ticks, fall back to original logic
									if (
										syncTicks &&
										(element.type === 'XAxis' || element.type === 'YAxis') &&
										element.props
									) {
										element.props.ticks = combinedTicks;
										element.props.tickCount = combinedTickCount;
									} else {
										applyTickPivotPoint(element, elements, composedChartProps);
									}
									applyLabelQueries(element, translationContext, queryStore);
									applyFormatters(element, translationContext, queryStore);
									applyCustomTickLabel(element);
									applySeriesLabels(element, translationContext);
									applyCustomTickValues(element, props);
									applyAnimationLogic(element, props);
									validateLegendElement(element, data);
									if (element.type === 'Legend' && element.props) {
										element.props.content = <DefaultLegendContent {...element.props} />;
									}
									if (element.type === 'Tooltip' && element.props) {
										element.props.content = <DefaultTooltipContent {...element.props} />;
									}
									const { type, props: elementProps = {} } = element;

									// Recharts Text element does not work so we need to implement a custom one
									if (type === 'Text') {
										const { label, scaleToFit, verticalAnchor, lineHeight, ...textProps } =
											elementProps as ITextProps;
										return (
											<text key={elementIndex} {...textProps}>
												{(elementProps as ITextProps).label}
											</text>
										);
									}
									// We will need to render a custom dot to handle the keyboard events,
									// if the dot is set to false then the dot should not render on the line
									// In that case we will render the dot, but will set the dot as transparent

									// We are only interested in rendering the series dataKeys and if they have a data point in the data,
									// Else the keyboard focus logic is going to highlight data points that is not relevant.
									const found = (data ?? []).find(
										(d) =>
											this.getSeriesDataKeys(chartProps).includes(
												(elementProps as any).dataKey,
											) && !!d[(elementProps as any).dataKey],
									);
									if (type === 'Line' && (elementProps as any).dot === false) {
										this.hideDots = true;
									}

									if (found) {
										if (type === 'Line') {
											(elementProps as any).dot = this.renderDotWrapper(translationContext, {
												xAxis,
												tooltip,
											});
										}

										if (type === 'Bar') {
											(elementProps as any).shape = this.renderRectangleWrapper(
												translationContext,
												{
													xAxis,
													tooltip,
												},
											);
										}
									}
									const RechartsElement = Recharts[type] as any;
									if ((element.type === 'XAxis' || element.type === 'YAxis') && element.props) {
										// TODO: Get aria props on the props interfaces
										(elementProps as any)['aria-hidden'] = true;
									}
									return (
										<RechartsElement key={elementIndex} {...elementProps}>
											{this.getLabelList(elementProps)}
											{type === 'ReferenceLine' && this.getLabel(elementProps)}
											{type === 'Bar' &&
												this.getCells(
													elementProps,
													composedChartProps && composedChartProps.data,
												)}
										</RechartsElement>
									);
								})}
							</ChartBaseComponent>
						</Recharts.ResponsiveContainer>
					</div>
				</Fragment>
				{disclaimersProps && (
					<DisclaimersWrapper>
						<Disclaimers {...disclaimersProps} />
					</DisclaimersWrapper>
				)}
			</Fragment>
		);
	};

	render() {
		const { className } = this.props;
		const breakpoints = this.breakpoints;
		return (
			<Fragment>
				<TranslationContext.Consumer>
					{(translationContext) => {
						return (
							<AccessibilityContext.Consumer>
								{(accessibilityContext) => {
									return breakpoints.map((breakpoint, breakpointIndex) => {
										const { props } = breakpoint;
										const breakpointProps = this.getBreakpointComponentProps(
											breakpoint,
											breakpointIndex,
										);
										return (
											<Breakpoint {...breakpointProps}>
												<Wrapper className={className}>
													{this.renderChart(props, translationContext, accessibilityContext)}
												</Wrapper>
											</Breakpoint>
										);
									});
								}}
							</AccessibilityContext.Consumer>
						);
					}}
				</TranslationContext.Consumer>
			</Fragment>
		);
	}
}

export default InjectedStyledComponent(observer(ChartBase), CHART_BASE_CATEGORY_KEY, CHART_BASE_COMPONENT_KEY);
